(function () {
  function initAISHooks() {
    if (window.__AIS_HOOKS_READY__) return;
    if (!window.algoliaShopify || !algoliaShopify.hooks || !algoliaShopify.hooks.registerHook) return;

    window.__AIS_HOOKS_READY__ = true;

    /* ──────────────────────────────────────────────────────────────
      Helpers
    ────────────────────────────────────────────────────────────── */
    const FACET_VISIBLE_LIMIT = 10;
    window.__AIS_EXTRA_TAG_FILTERS__ = window.__AIS_EXTRA_TAG_FILTERS__ || new Set();

    function escapeAlgoliaFilterValue(value) {
      return String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    }

    function ensureDisjunctiveFacets() {
      const helper = algoliaShopify?.instantsearch?.search?.helper;
      if (!helper?.state || typeof helper.setState !== "function") return false;

      const wantedDisjunctive = ["vendor", "product_type", "price"];

      let nextState = helper.state;
      let changed = false;

      wantedDisjunctive.forEach((facet) => {
        const current = Array.isArray(nextState.disjunctiveFacets)
          ? nextState.disjunctiveFacets
          : [];

        if (current.includes(facet)) return;

        try {
          nextState = nextState.addDisjunctiveFacet(facet);
          changed = true;
        } catch (e) {
          console.warn(`[AIS] could not add disjunctive facet "${facet}"`, e);
        }
      });

      if (changed) {
        helper.setState(nextState);
      }

      return changed;
    }

    function buildShowMoreFooter(hiddenCount, key) {
      if (hiddenCount <= 0) return "";

      return `
        <button
          type="button"
          class="ais-facet-show-more"
          data-facet-show-more="${escapeHtml(key)}"
          aria-expanded="false"
        >
          + Read more (${hiddenCount})
        </button>
      `;
    }

    function hasAISLayout() {
      return !!document.querySelector(
        ".ais-page, .ais-facets, .ais-hits-container, .ais-SearchBox-input, .ais-search-box"
      );
    }

    function safeQuery(sel) {
      try {
        return document.querySelector(sel);
      } catch {
        return null;
      }
    }

    function waitForAIS(timeout = 4000) {
      return new Promise((resolve, reject) => {
        const start = Date.now();
        (function tick() {
          if (hasAISLayout()) return resolve(true);
          if (Date.now() - start > timeout) return reject(new Error("AIS timeout"));
          requestAnimationFrame(tick);
        })();
      });
    }

    function getBaseProductsIndexName() {
      const fromConfig =
        algoliaShopify?.config?.indexName || algoliaShopify?.config?.index_name;
      if (fromConfig) return String(fromConfig);

      try {
        const search = algoliaShopify?.instantsearch?.search;
        const main = search?.mainIndex;
        const name = main?.getIndexName?.();
        if (name) return String(name);
      } catch {}

      return "products";
    }

    function slugify(str) {
      return String(str || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    }

    function escapeHtml(str) {
      return String(str ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    }

    function getFacetByName(facets, name) {
      return (facets || []).find((f) => f && f.name === name) || null;
    }

    function getPriceStats() {
      const r = algoliaShopify?.instantsearch?.search?.helper?.lastResults;
      if (!r) return null;

      const fromDisj = (r.disjunctiveFacets || []).find((f) => f.name === "price");
      if (fromDisj?.stats) return fromDisj.stats;

      const fromFacets = (r.facets || []).find((f) => f.name === "price");
      if (fromFacets?.stats) return fromFacets.stats;

      return null;
    }

    function getCurrentPriceRefinements() {
      const state = algoliaShopify?.instantsearch?.search?.helper?.state;
      const nr = state?.numericRefinements?.price || {};
      const min = Array.isArray(nr[">="]) ? Number(nr[">="][0]) : null;
      const max = Array.isArray(nr["<="]) ? Number(nr["<="][0]) : null;
      return { min, max };
    }

    function parsePriceRangeLabel(label) {
      const nums = (String(label).match(/[\d]+(?:[.,]\d+)?/g) || []).map((n) =>
        Number(n.replace(",", ""))
      );

      if (nums.length === 0) return { start: null, end: null };
      if (nums.length === 1) {
        if (/under|less|menos|hasta/i.test(label)) return { start: 0, end: nums[0] };
        return { start: nums[0], end: null };
      }

      return { start: nums[0], end: nums[1] };
    }

    function getPriceRangeBuckets() {
      const r = algoliaShopify?.instantsearch?.search?.helper?.lastResults;
      if (!r) return [];

      const all = [...(r.disjunctiveFacets || []), ...(r.facets || [])];
      const facet = all.find((f) => f && f.name === "price_range");
      if (!facet?.data) return [];

      return Object.entries(facet.data)
        .map(([label, count]) => ({ count: Number(count) || 0, ...parsePriceRangeLabel(label) }))
        .filter((b) => b.start !== null)
        .sort((a, b) => a.start - b.start);
    }

    function naturalCompare(a, b) {
      return String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: "base",
      });
    }

    function formatCount(n) {
      const num = Number(n || 0);
      if (!Number.isFinite(num)) return String(n || "");
      return num.toLocaleString("es-CO").replace(/,/g, ".");
    }

    function parseTagGroup(rawValue) {
      let value = String(rawValue || "").trim();
      if (!value) return null;

      value = value.replace(/\d{1,3}(?:\.\d{3})+$/, "").trim();

      const matchers = [
        {
          key: "type-tires",
          title: "Type Tires",
          re: /^(light truck tires|passenger tires)$/i,
          format: (m) => m[1] || m[0],
        },
        {
          key: "category",
          title: "Category",
          re: /^category\s+(.+)$/i,
          format: (m) => m[1],
        },
        {
          key: "rim-size",
          title: "Rim Size",
          re: /^r\s*(\d{2,3})$/i,
          format: (m) => `R${m[1]}`,
        },
        {
          key: "speed",
          title: "Speed",
          re: /^speed\s+(.+)$/i,
          format: (m) => m[1],
        },
        {
          key: "tread",
          title: "Tread",
          re: /^tread\s+(.+)$/i,
          format: (m) => m[1],
        },
        {
          key: "aspect",
          title: "Aspect",
          re: /^aspect\s+(.+)$/i,
          format: (m) => m[1],
        },
        {
          key: "section",
          title: "Section",
          re: /^section\s+(.+)$/i,
          format: (m) => m[1],
        },
        {
          key: "load",
          title: "Load",
          re: /^load\s+(.+)$/i,
          format: (m) => m[1],
        },
      ];

      for (const rule of matchers) {
        const m = value.match(rule.re);
        if (m) {
          return {
            groupKey: rule.key,
            groupTitle: rule.title,
            rawValue: value,
            label: typeof rule.format === "function" ? rule.format(m) : value,
          };
        }
      }

      return null;
    }

    function isFacetValueSelected(facetName, value) {
      if (facetName === "tags") {
        return window.__AIS_EXTRA_TAG_FILTERS__?.has(value) || false;
      }

      const state = algoliaShopify?.instantsearch?.search?.helper?.state;
      if (!state) return false;

      const disj = state.disjunctiveFacetsRefinements?.[facetName] || [];
      const conj = state.facetsRefinements?.[facetName] || [];

      return disj.includes(value) || conj.includes(value);
    }

    function buildDedupedSortedItems(items) {
      const map = new Map();

      (items || []).forEach((item) => {
        const key = String(item.rawValue || item.label || "").toLowerCase();
        if (!key) return;

        if (!map.has(key)) {
          map.set(key, item);
        } else {
          const prev = map.get(key);
          map.set(key, {
            ...prev,
            count: Math.max(Number(prev.count || 0), Number(item.count || 0)),
            checked: !!prev.checked || !!item.checked,
          });
        }
      });

      return [...map.values()].sort((a, b) => naturalCompare(a.label, b.label));
    }

    function buildSearchOnceState(excludeFacet) {
      const helper = algoliaShopify?.instantsearch?.search?.helper;
      let state = helper?.state;
      if (!state) return null;

      try {
        state = state.setPage(0);
      } catch {}

      try {
        state = state.setQueryParameter("hitsPerPage", 0);
      } catch {}

      try {
        state = state.setQueryParameter("maxValuesPerFacet", 1000);
      } catch {}

      try {
        state = state.setQueryParameter("facetingAfterDistinct", true);
      } catch {}

      try {
        state = state.setQueryParameter("analytics", false);
      } catch {}

      try {
        state = state.setQueryParameter("clickAnalytics", false);
      } catch {}

      /*try {
        state = state.setQueryParameter("facets", [excludeFacet]);
      } catch {}*/

      // Quitamos solo los refinements del facet que queremos recalcular
      try {
        const disj = state.disjunctiveFacetsRefinements?.[excludeFacet] || [];
        disj.forEach((v) => {
          state = state.removeDisjunctiveFacetRefinement(excludeFacet, v);
        });
      } catch {}

      try {
        const conj = state.facetsRefinements?.[excludeFacet] || [];
        conj.forEach((v) => {
          state = state.removeFacetRefinement(excludeFacet, v);
        });
      } catch {}

      try {
        const currentFacets = Array.isArray(state.facets) ? state.facets : [];

        if (!currentFacets.includes(excludeFacet)) {
          state = state.addFacet(excludeFacet);
        }
      } catch {}

      try {
        const queryFacets = state.queryParameters?.facets || [];
        const nextFacets = Array.from(
          new Set([
            ...(Array.isArray(queryFacets) ? queryFacets : []),
            excludeFacet,
          ])
        );

        state = state.setQueryParameter("facets", nextFacets);
      } catch {}

      return state;
    }

    async function searchFacetOnce(facetName) {
      const helper = algoliaShopify?.instantsearch?.search?.helper;
      if (!helper || typeof helper.searchOnce !== "function") return {};

      const state = buildSearchOnceState(facetName);
      if (!state) return {};

      try {
        const res = await helper.searchOnce(state);

        const facetsArray = [
          ...(res?.content?.facets || []),
          ...(res?.content?.disjunctiveFacets || []),
          ...(res?.results?.facets || []),
          ...(res?.results?.disjunctiveFacets || []),
        ];

        if (!Array.isArray(facetsArray)) {
          console.warn(`[AIS] ${facetName} facets array not found`, res);
          return {};
        }

        const facet = facetsArray.find((f) => f.name === facetName);
        const data = facet?.data || {};

        console.log(`[AIS] ${facetName} facets`, data);

        return data;
      } catch (e) {
        console.warn(`[AIS] searchOnce failed for facet "${facetName}"`, e);
        return {};
      }
    }

    let __AIS_DIRECT_SEQ__ = 0;

    async function fetchDirectFacetData() {
      const seq = ++__AIS_DIRECT_SEQ__;

      const [vendor, product_type, tags] = await Promise.all([
        searchFacetOnce("vendor"),
        searchFacetOnce("product_type"),
        searchFacetOnce("tags"),
      ]);

      if (seq !== __AIS_DIRECT_SEQ__) return null;

      return { vendor, product_type, tags };
    }

    function bindDirectFacetCheckboxes(root) {
      if (!root) return;

      root.querySelectorAll(".ais-direct-facet-checkbox").forEach((cb) => {
        if (cb.dataset.bound === "1") return;
        cb.dataset.bound = "1";
      });
    }

    function renderDirectFacetList({ facetName, hostSelector, data }) {
      const host = document.querySelector(hostSelector);
      if (!host) return;

      const items = buildDedupedSortedItems(
        Object.entries(data || {}).map(([label, count]) => ({
          label,
          rawValue: label,
          count,
          checked: isFacetValueSelected(facetName, label),
        }))
      );

      const wrapper = host.closest(".ais-facet-dropdown-wrapper");

      if (!items.length) {
        host.innerHTML = "";
        if (wrapper) wrapper.style.display = "none";
        return;
      }

      if (wrapper) wrapper.style.display = "";

      const expanded = host.dataset.expanded === "1";
      const visibleItems = expanded ? items : items.slice(0, FACET_VISIBLE_LIMIT);
      const hiddenCount = Math.max(0, items.length - FACET_VISIBLE_LIMIT);

      host.innerHTML = `
        <div class="ais-facet-items-list">
          ${visibleItems
            .map((item, idx) => {
              const id = `${facetName}-${slugify(item.rawValue)}-${idx}`;
              return `
                <label class="ais-custom-refinement-item ${item.checked ? "is-selected" : ""}" for="${id}">
                  <input
                    id="${id}"
                    type="checkbox"
                    class="ais-direct-facet-checkbox"
                    data-facet="${escapeHtml(facetName)}"
                    data-value="${escapeHtml(item.rawValue)}"
                    ${item.checked ? "checked" : ""}
                  />
                  <span class="ais-custom-refinement-label">${escapeHtml(item.label)}</span>
                  <span class="ais-custom-refinement-count">(${escapeHtml(formatCount(item.count))})</span>
                </label>
              `;
            })
            .join("")}
        </div>
        ${!expanded ? buildShowMoreFooter(hiddenCount, facetName) : ""}
      `;

      bindDirectFacetCheckboxes(host);

      const btn = host.querySelector(`[data-facet-show-more="${facetName}"]`);
      if (btn && !btn.dataset.bound) {
        btn.dataset.bound = "1";
        btn.addEventListener("click", () => {
          host.dataset.expanded = "1";
          renderDirectFacetList({ facetName, hostSelector, data });
        });
      }
    }

    function groupDirectTags(tagsObj) {
      const grouped = {
        "type-tires": [],
        "rim-size": [],
        speed: [],
        tread: [],
        aspect: [],
        section: [],
        load: [],
      };

      Object.entries(tagsObj || {}).forEach(([rawTag, count]) => {
        const parsed = parseTagGroup(rawTag);
        if (!parsed) return;
        if (parsed.groupKey === "category") return;

        if (!grouped[parsed.groupKey]) grouped[parsed.groupKey] = [];
        grouped[parsed.groupKey].push({
          rawValue: rawTag,
          label: parsed.label,
          count,
          checked: isFacetValueSelected("tags", rawTag),
        });
      });

      Object.keys(grouped).forEach((key) => {
        grouped[key] = buildDedupedSortedItems(grouped[key]);
      });

      return grouped;
    }

    function renderDirectGroupedTags(grouped) {
      const order = [
        ["type-tires", "Type Tires"],
        ["rim-size", "Rim Size"],
        ["speed", "Speed"],
        ["tread", "Tread"],
        ["aspect", "Aspect"],
        ["section", "Section"],
        ["load", "Load"],
      ];

      order.forEach(([key]) => {
        const wrapper = document.querySelector(`[data-custom-facet="${key}"]`);
        const container = wrapper?.querySelector(".ais-custom-facet-container");
        if (!wrapper || !container) return;

        const items = grouped[key] || [];
        if (!items.length) {
          wrapper.style.display = "none";
          container.innerHTML = "";
          return;
        }

        wrapper.style.display = "";

        const expanded = container.dataset.expanded === "1";
        const visibleItems = expanded ? items : items.slice(0, FACET_VISIBLE_LIMIT);
        const hiddenCount = Math.max(0, items.length - FACET_VISIBLE_LIMIT);

        container.innerHTML = `
          <div class="ais-facet-items-list">
            ${visibleItems
              .map((item, idx) => {
                const id = `${key}-${slugify(item.rawValue)}-${idx}`;
                return `
                  <label class="ais-custom-refinement-item ${item.checked ? "is-selected" : ""}" for="${id}">
                    <input
                      id="${id}"
                      type="checkbox"
                      class="ais-direct-facet-checkbox"
                      data-facet="tags"
                      data-value="${escapeHtml(item.rawValue)}"
                      ${item.checked ? "checked" : ""}
                    />
                    <span class="ais-custom-refinement-label">${escapeHtml(item.label)}</span>
                    <span class="ais-custom-refinement-count">(${escapeHtml(formatCount(item.count))})</span>
                  </label>
                `;
              })
              .join("")}
          </div>
          ${!expanded ? buildShowMoreFooter(hiddenCount, key) : ""}
        `;

        bindDirectFacetCheckboxes(container);

        const btn = container.querySelector(`[data-facet-show-more="${key}"]`);
        if (btn && !btn.dataset.bound) {
          btn.dataset.bound = "1";
          btn.addEventListener("click", () => {
            container.dataset.expanded = "1";
            renderDirectGroupedTags(grouped);
          });
        }
      });
    }

    async function syncDirectFacetsFromAlgolia() {
      const data = await fetchDirectFacetData();
      if (!data) return;

      renderDirectFacetList({
        facetName: "vendor",
        hostSelector: '[data-direct-facet-host="vendor"]',
        data: data.vendor,
      });

      renderDirectFacetList({
        facetName: "product_type",
        hostSelector: '[data-direct-facet-host="product_type"]',
        data: data.product_type,
      });

      if (data.tags && Object.keys(data.tags).length) {
        window.__AIS_LAST_TAGS_DATA__ = data.tags;
      }

      const tagsData =
        data.tags && Object.keys(data.tags).length
          ? data.tags
          : window.__AIS_LAST_TAGS_DATA__;

      if (tagsData && Object.keys(tagsData).length) {
        const grouped = groupDirectTags(tagsData);
        renderDirectGroupedTags(grouped);
      }
    }

    function renderCustomSelectedTagRefinements() {
      const container = document.querySelector(".ais-current-refined-values-container");
      if (!container) return;

      const state = algoliaShopify?.instantsearch?.search?.helper?.state;
      if (!state) return;

      const selectedTags = Array.from(window.__AIS_EXTRA_TAG_FILTERS__ || []);

      container.querySelectorAll("[data-custom-selected-tag]").forEach((el) => el.remove());

      selectedTags.forEach((tag) => {
        const parsed = parseTagGroup(tag);
        if (!parsed) return;

        const item = document.createElement("div");
        item.className = "ais-current-refined-values--item";
        item.setAttribute("data-custom-selected-tag", tag);

        item.innerHTML = `
          <span class="ais-current-refined-values--clear">×</span>
          <span class="ais-current-refined-values--label">
            ${escapeHtml(parsed.groupTitle)} : ${escapeHtml(parsed.label)}
          </span>
        `;

        item.addEventListener("click", () => {
          const helper = algoliaShopify?.instantsearch?.search?.helper;
          if (!helper) return;

          let state = helper.state;

          try {
            state = state.removeFacetRefinement("tags", tag);
          } catch {}

          const cleanFilters = String(state.filters || "")
            .split(" AND ")
            .filter((part) => {
              const safeTag = String(tag).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
              return part !== `tags:"${safeTag}"`;
            })
            .join(" AND ");

          state = state.setQueryParameter("filters", cleanFilters || undefined);
          state = state.setPage(0);

          helper.setState(state);
          helper.search();

          setTimeout(() => {
            window.dispatchEvent(new CustomEvent("ais-filter-applied"));
            window.dispatchEvent(new CustomEvent("ais-rendered"));
          }, 100);
        });

        const list =
          container.querySelector(".ais-current-refined-values--list") ||
          container.querySelector("ul") ||
          container;

        list.appendChild(item);
      });
    }

    function syncCurrentRefinementLabels() {
      document
        .querySelectorAll(".ais-current-refined-values--item, .ais-CurrentRefinements-item")
        .forEach((item) => {
          const labelEl =
            item.querySelector(".ais-current-refined-values--label") ||
            item.querySelector(".ais-CurrentRefinements-label") ||
            item;

          const txt = (labelEl.textContent || "").trim();
          if (!txt) return;

          const parsed = parseTagGroup(txt);
          if (parsed) {
            labelEl.textContent = `${parsed.groupTitle}: ${parsed.label}`;
            return;
          }

          if (/^product_type\s*:\s*/i.test(txt)) {
            labelEl.textContent = txt.replace(/^product_type\s*:\s*/i, "Category: ");
          }
        });
    }

    function renderCustomPriceRange() {
      const host = document.querySelector("[data-price-range-host]");
      if (!host) return;

      const stats = getPriceStats();
      if (!stats || typeof stats.min !== "number" || typeof stats.max !== "number") {
        host.innerHTML = "";
        return;
      }

      const absMin = Math.floor(stats.min);
      const absMax = Math.ceil(stats.max);
      const current = getCurrentPriceRefinements();

      const startMin = Number.isFinite(current.min)
        ? Math.max(absMin, Math.floor(current.min))
        : absMin;
      const startMax = Number.isFinite(current.max)
        ? Math.min(absMax, Math.ceil(current.max))
        : absMax;

      const buckets = getPriceRangeBuckets();
      const maxCount = buckets.reduce((m, b) => Math.max(m, b.count), 0);

      const bars =
        buckets.length > 0
          ? buckets.map((b) => ({
              heightPct: maxCount > 0 ? Math.max(6, Math.round((b.count / maxCount) * 100)) : 6,
              start: b.start,
              end: b.end == null ? absMax : b.end,
            }))
          : Array.from({ length: 20 }, (_, i) => ({
              heightPct: 18,
              start: absMin + (i / 20) * (absMax - absMin),
              end: absMin + ((i + 1) / 20) * (absMax - absMin),
            }));

      const barsHtml = bars
        .map((bar) => `<div class="ais-price-bar" style="height:${bar.heightPct}%"></div>`)
        .join("");

      host.innerHTML = `
        <div class="ais-price-range">
          <div class="ais-price-histogram" data-price-histogram>${barsHtml}</div>

          <div class="ais-price-slider-track">
            <div class="ais-price-range-base"></div>
            <div class="ais-price-range-fill" data-price-fill></div>
            <input type="range" class="ais-price-range-input" min="${absMin}" max="${absMax}" step="1" value="${startMin}" data-price-range="min" />
            <input type="range" class="ais-price-range-input" min="${absMin}" max="${absMax}" step="1" value="${startMax}" data-price-range="max" />
          </div>

          <div class="ais-price-range-values">
            <div class="ais-price-value-box">
              <span>Min</span>
              <input type="number" min="${absMin}" max="${absMax}" step="1" value="${startMin}" data-price-number="min" />
            </div>
            <div class="ais-price-value-box">
              <span>Max</span>
              <input type="number" min="${absMin}" max="${absMax}" step="1" value="${startMax}" data-price-number="max" />
            </div>
          </div>
        </div>
      `;

      const numMin = host.querySelector('[data-price-number="min"]');
      const numMax = host.querySelector('[data-price-number="max"]');
      const rangeMin = host.querySelector('[data-price-range="min"]');
      const rangeMax = host.querySelector('[data-price-range="max"]');
      const fill = host.querySelector("[data-price-fill]");
      const histogram = host.querySelector("[data-price-histogram]");
      const barEls = histogram ? Array.from(histogram.children) : [];

      const toPct = (value) => ((value - absMin) / (absMax - absMin)) * 100;

      const updateUI = () => {
        const min = Number(rangeMin.value);
        const max = Number(rangeMax.value);

        rangeMin.max = String(max);
        rangeMax.min = String(min);

        fill.style.left = `${toPct(min)}%`;
        fill.style.right = `${100 - toPct(max)}%`;

        numMin.value = String(min);
        numMax.value = String(max);

        barEls.forEach((barEl, i) => {
          const b = bars[i];
          barEl.classList.toggle("is-in-range", b.end > min && b.start < max);
        });
      };

      const apply = () => {
        const helper = algoliaShopify?.instantsearch?.search?.helper;
        if (!helper) return;

        const min = Number(rangeMin.value);
        const max = Number(rangeMax.value);

        try {
          helper.removeNumericRefinement("price");
        } catch {}

        if (min > absMin) helper.addNumericRefinement("price", ">=", min);
        if (max < absMax) helper.addNumericRefinement("price", "<=", max);

        helper.search();
      };

      const setFromNumber = (source) => {
        let min = Number(numMin.value || absMin);
        let max = Number(numMax.value || absMax);

        min = Math.max(absMin, Math.min(min, absMax));
        max = Math.max(absMin, Math.min(max, absMax));

        if (min > max) {
          if (source === "numMin") max = min;
          else min = max;
        }

        rangeMin.value = String(min);
        rangeMax.value = String(max);
        updateUI();
        apply();
      };

      rangeMin.addEventListener("input", updateUI);
      rangeMax.addEventListener("input", updateUI);
      rangeMin.addEventListener("change", apply);
      rangeMax.addEventListener("change", apply);

      numMin.addEventListener("change", () => setFromNumber("numMin"));
      numMax.addEventListener("change", () => setFromNumber("numMax"));

      updateUI();
    }

    /* ──────────────────────────────────────────────────────────────
      OPTIONS
    ────────────────────────────────────────────────────────────── */
    algoliaShopify.hooks.registerHook("beforeInstantSearchOptions", function (options) {
      options.colors.main = "#121212";
      options.colors.highlight = "#ffffff";

      options.searchParameters = options.searchParameters || {};
      options.searchParameters.maxValuesPerFacet = 1000;
      options.searchParameters.facetingAfterDistinct = true;

      options.searchParameters.facets = Array.from(
        new Set([
          ...(options.searchParameters.facets || []),
          "tags",
          "vendor",
          "product_type",
          "price",
          "price_range",
        ])
      );

      // En collections, el router de Algolia/Shopify reescribe la URL
      // y elimina refinementList[tags]. Desactivamos routing para que el
      // estado se mantenga en el helper de InstantSearch.
      options.routing = false;

      return options;
    });

    algoliaShopify.hooks.registerHook("beforeInstantSearchFiltersString", function (filters) {
      const tagFilters = Array.from(window.__AIS_EXTRA_TAG_FILTERS__ || []).map(
        (tag) => `tags:"${escapeAlgoliaFilterValue(tag)}"`
      );

      if (!tagFilters.length) return filters;

      const extra = tagFilters.join(" AND ");

      return filters ? `(${filters}) AND ${extra}` : extra;
    });

    algoliaShopify.hooks.registerHook("beforeInstantSearchFiltersString", function (filters) {
      if (window.location.pathname.includes("pre-order-fara-drift-warz")) return filters;
      const exclude = 'NOT tags:"Pre-Order"';
      return filters ? `(${filters}) AND ${exclude}` : exclude;
    });

    /* ──────────────────────────────────────────────────────────────
      SORT ORDERS
    ────────────────────────────────────────────────────────────── 
    algoliaShopify.hooks.registerHook("beforeInstantSearchSortOrders", function () {
      const base = getBaseProductsIndexName();
      const asc = `${base}_price_asc`;
      const desc = `${base}_price_desc`;

      return [
        { label: "Relevance", value: base },
        { label: "Price: Low to High", value: asc },
        { label: "Price: High to Low", value: desc },
      ];
    });*/

    function getSortSelect() {
      return (
        document.querySelector(".ais-sort-orders-container select") ||
        document.querySelector(".ais-sort select")
      );
    }

    function ensurePriceSortOptions() {
      const sel = getSortSelect();
      const search = algoliaShopify?.instantsearch?.search;
      if (!sel || !search || !search.helper) return;

      const base = getBaseProductsIndexName();
      const idxAsc = `${base}_price_asc`;
      const idxDesc = `${base}_price_desc`;

      const hasAsc = Array.from(sel.options).some((o) => o.value === idxAsc);
      const hasDesc = Array.from(sel.options).some((o) => o.value === idxDesc);

      if (!hasAsc) {
        const opt = document.createElement("option");
        opt.value = idxAsc;
        opt.textContent = "Price: Low to High";
        sel.appendChild(opt);
      }

      if (!hasDesc) {
        const opt = document.createElement("option");
        opt.value = idxDesc;
        opt.textContent = "Price: High to Low";
        sel.appendChild(opt);
      }

      if (!sel.dataset.priceSortBound) {
        sel.dataset.priceSortBound = "1";

        sel.addEventListener("change", () => {
          const val = sel.value;
          const allowed = new Set([base, idxAsc, idxDesc]);
          if (!allowed.has(val)) return;

          try {
            search.helper.setIndex(val);
            search.helper.search();
          } catch (e) {
            console.warn("[AIS] sort setIndex failed", e);
          }
        });
      }
    }

    /* ──────────────────────────────────────────────────────────────
      FACETS UI
    ────────────────────────────────────────────────────────────── */
    const facetCollapseState = (window.__AIS_FACET_COLLAPSE_STATE__ =
      window.__AIS_FACET_COLLAPSE_STATE__ || {});

    function applyFacetCollapseState() {
      if (!hasAISLayout()) return;

      document.querySelectorAll(".ais-facets .ais-dropdown-checkbox").forEach((cb) => {
        const id = cb.id;

        if (id in facetCollapseState) {
          cb.checked = facetCollapseState[id];
        } else {
          facetCollapseState[id] = cb.checked;
        }

        if (!cb.dataset.collapseBound) {
          cb.dataset.collapseBound = "1";
          cb.addEventListener("change", () => {
            facetCollapseState[cb.id] = cb.checked;
          });
        }
      });
    }

    function applyFacetUIFixes() {
      if (!hasAISLayout()) return;

      applyFacetCollapseState();

      const changed = ensureDisjunctiveFacets();
      if (changed) {
        try {
          algoliaShopify?.instantsearch?.search?.helper?.search();
          return;
        } catch {}
      }

      syncCurrentRefinementLabels();
      renderCustomSelectedTagRefinements();
      syncDirectFacetsFromAlgolia();
      renderCustomPriceRange();
    }

    /* ──────────────────────────────────────────────────────────────
      CSS
    ────────────────────────────────────────────────────────────── */
    (function injectFacetCss() {
      if (document.querySelector('style[data-ais-force-open="1"]')) return;

      const css = `
      .ais-facets .ais-dropdown-checkbox { display:none !important; }

      .ais-facets .ais-facet-dropdown-container {
        overflow:hidden !important;
        max-height:1000px !important;
        opacity:1 !important;
        margin-top:10px !important;
        transition:max-height .25s ease, opacity .2s ease, margin-top .2s ease !important;
      }

      /* La hoja de estilos de la app de Algolia oculta .ais-facet-dropdown-container
         con display:none cuando su checkbox hermano esta :checked (para ella,
         checked = colapsado). Nuestro modelo es al reves (checked = abierto),
         asi que forzamos display:block para neutralizar esa regla externa. */
      .ais-facets .ais-facet-dropdown-wrapper:has(.ais-dropdown-checkbox:checked) .ais-facet-dropdown-container {
        display:block !important;
      }

      .ais-facets .ais-facet-dropdown-wrapper:has(.ais-dropdown-checkbox:not(:checked)) .ais-facet-dropdown-container {
        max-height:0 !important;
        opacity:0 !important;
        margin-top:0 !important;
      }

      .ais-facets .ais-range-slider--header {
        cursor:pointer !important;
        display:flex !important;
        align-items:center !important;
        justify-content:space-between !important;
        user-select:none !important;
      }

      .ais-facets .ais-range-slider--header::after {
        content:"" !important;
        width:10px !important;
        height:10px !important;
        margin-left:8px !important;
        flex-shrink:0 !important;
        background-image:var(--icon-caret) !important;
        background-size:contain !important;
        background-repeat:no-repeat !important;
        background-position:center !important;
        transition:transform .2s ease !important;
      }

      .ais-facets .ais-facet-dropdown-wrapper:has(.ais-dropdown-checkbox:not(:checked)) .ais-range-slider--header::after {
        transform:rotate(180deg) !important;
      }
        .ais-facet-show-more{
          margin-top:6px !important;
          background:transparent !important;
          border:none !important;
          color:#C1272D !important;
          font-size:13px !important;
          font-weight:700 !important;
          cursor:pointer !important;
          padding:4px 2px !important;
          text-align:left !important;
        }

        .ais-facet-show-more:hover{
          text-decoration:underline !important;
        }
        .ais-direct-facet-checkbox{
        border-radius: 25px !important;
        width: 10px !important;
        height: 10px !important;
        border-color: #C1272D !important;
        color: #C1272D !important;
        }
      .ais-facets .ais-facet-dropdown-wrapper {
        height:auto !important;
        color:#000 !important;
        padding:10px 0 !important;
      }

      .ais-facet-source-hidden { display:none !important; }

      .ais-custom-refinement-checkbox{
        border-radius:25px !important;
        width:10px !important;
        height:10px !important;
        border-color:#C1272D !important;
        color:#C1272D !important;
      }

      .ais-sort select,
      .ais-sort-orders-container select {
        min-width:220px;
      }

      .ais-input.fitment-anchor{ position:relative !important; }

      .ais-input.fitment-anchor .fitment-suggestions{
        position:absolute !important;
        left:20% !important;
        right:0 !important;
        top:80px !important;
        width:60% !important;
        background:#fff !important;
        border:1px solid rgba(0,0,0,.10) !important;
        border-radius:14px !important;
        box-shadow:0 18px 45px rgba(0,0,0,.14) !important;
        z-index:999999 !important;
        padding:12px !important;
      }

      @media (max-width: 720px){
        .ais-input.fitment-anchor .fitment-suggestions{
          left:4% !important;
          right:0 !important;
          top:60px !important;
          width:92% !important;
        }
      }

      .ais-input.fitment-anchor .fitment-items{
        max-height:280px !important;
        overflow:auto !important;
        -webkit-overflow-scrolling:touch !important;
        padding-right:4px !important;
      }

      .fitment-title{
        font-weight:700 !important;
        font-size:12px !important;
        text-transform:uppercase !important;
        opacity:.75 !important;
        margin-bottom:8px !important;
        letter-spacing:.04em !important;
      }

      .fitment-item{
        display:flex !important;
        justify-content:space-between !important;
        align-items:flex-start !important;
        gap:10px !important;
        padding:10px !important;
        border-radius:12px !important;
        cursor:pointer !important;
        user-select:none !important;
        -webkit-tap-highlight-color:transparent !important;
      }

      .fitment-item:hover{ background:rgba(0,0,0,.05) !important; }

      .fitment-label{
        font-weight:650 !important;
        font-size:13px !important;
        line-height:1.25 !important;
        color:#111 !important;
        overflow:hidden !important;
        text-overflow:ellipsis !important;
        white-space:nowrap !important;
        max-width:100% !important;
      }

      .fitment-meta{
        font-size:12px !important;
        color:rgba(0,0,0,.55) !important;
        margin-top:2px !important;
        line-height:1.25 !important;
        overflow:hidden !important;
        text-overflow:ellipsis !important;
        white-space:nowrap !important;
        max-width:100% !important;
      }

      .fitment-size{
        font-weight:750 !important;
        font-size:12px !important;
        color:#111 !important;
        white-space:nowrap !important;
        padding-top:2px !important;
      }

      .improved-search .ais-page .ais-change-display{
        border-radius:8px !important;
      }

      .improved-search .ais-facets .ais-facet-dropdown-wrapper{
        padding-top:5px !important;
      }

      .ais-SortBy select{
        padding:0 10px !important;
        height:2.2em !important;
        border:1px solid #A6A6A6 !important;
        border-radius:8px;
      }

      .improved-search .ais-page .ais-change-display-block{
        border-radius:8px 0 0 8px !important;
      }

      .improved-search .ais-page .ais-change-display-block:before{
        border-radius:8px 0 0 8px !important;
        background-image:url('https://dantetires.com/cdn/shop/t/38/assets/icon-grid.svg.liquid?v=50279761483712260721750369456');
      }

      .improved-search .ais-page .ais-change-display-list{
        border-radius:0 8px 8px 0 !important;
      }

      .improved-search .ais-page .ais-change-display-list:before{
        border-radius:0 8px 8px 0 !important;
        background-image:url('https://dantetires.com/cdn/shop/t/38/assets/icon-list.svg.liquid?v=180663374482027377151750369474');
      }

      .ais-custom-facet-container{
        display:flex !important;
        flex-direction:column !important;
        gap:8px !important;
      }

      .ais-custom-refinement-item{
        display:flex !important;
        align-items:center !important;
        gap:10px !important;
        font-size:14px !important;
        color:#111 !important;
        cursor:pointer !important;
        padding:2px 10px !important;
        border-radius:999px !important;
        border:1px solid transparent !important;
        transition:all .18s ease !important;
      }

      .ais-custom-refinement-item:hover{
        background:#fff5f5 !important;
        border-color:#f3b3b3 !important;
      }

      .ais-custom-refinement-item.is-selected{
        background:#fff0f0 !important;
        border-color:#dc2626 !important;
      }

      .ais-custom-refinement-item input{
        width:16px !important;
        height:16px !important;
        margin:0 !important;
        cursor:pointer !important;
        accent-color:#dc2626 !important;
      }

      .ais-custom-refinement-label{
        line-height:1.2 !important;
        font-weight:500 !important;
      }

      .ais-custom-refinement-count{
        color:#6a6a6a !important;
        font-size:13px !important;
        margin-left:auto !important;
      }

      .ais-price-range{
        display:flex !important;
        flex-direction:column !important;
        gap:10px !important;
        padding-top:6px !important;
      }

      .ais-price-histogram{
        display:flex !important;
        align-items:flex-end !important;
        gap:2px !important;
        height:48px !important;
      }

      .ais-price-bar{
        flex:1 1 0 !important;
        min-width:2px !important;
        background:#e6e1e1 !important;
        border-radius:2px 2px 0 0 !important;
        transition:background .15s ease !important;
      }

      .ais-price-bar.is-in-range{
        background:#C1272D !important;
      }

      .ais-price-slider-track{
        position:relative !important;
        height:20px !important;
      }

      .ais-price-range-base{
        position:absolute !important;
        left:0 !important;
        right:0 !important;
        top:50% !important;
        height:4px !important;
        background:#e0e0e0 !important;
        border-radius:2px !important;
        transform:translateY(-50%) !important;
      }

      .ais-price-range-fill{
        position:absolute !important;
        top:50% !important;
        height:4px !important;
        background:#C1272D !important;
        border-radius:2px !important;
        transform:translateY(-50%) !important;
      }

      .ais-price-range-input{
        position:absolute !important;
        left:0 !important;
        top:50% !important;
        width:100% !important;
        margin:0 !important;
        background:transparent !important;
        -webkit-appearance:none !important;
        appearance:none !important;
        pointer-events:none !important;
        transform:translateY(-50%) !important;
      }

      .ais-price-range-input::-webkit-slider-runnable-track{
        background:transparent !important;
      }

      .ais-price-range-input::-moz-range-track{
        background:transparent !important;
      }

      .ais-price-range-input::-webkit-slider-thumb{
        pointer-events:auto !important;
        -webkit-appearance:none !important;
        width:18px !important;
        height:18px !important;
        border-radius:50% !important;
        background:#fff !important;
        border:2px solid #C1272D !important;
        box-shadow:0 1px 3px rgba(0,0,0,.3) !important;
        cursor:pointer !important;
      }

      .ais-price-range-input::-moz-range-thumb{
        pointer-events:auto !important;
        width:18px !important;
        height:18px !important;
        border-radius:50% !important;
        background:#fff !important;
        border:2px solid #C1272D !important;
        box-shadow:0 1px 3px rgba(0,0,0,.3) !important;
        cursor:pointer !important;
      }

      .ais-price-range-values{
        display:flex !important;
        align-items:center !important;
        gap:10px !important;
      }

      .ais-price-value-box{
        flex:1 1 0 !important;
        display:flex !important;
        flex-direction:column !important;
        gap:4px !important;
      }

      .ais-price-value-box span{
        font-size:12px !important;
        color:#6a6a6a !important;
        font-weight:600 !important;
      }

      .ais-price-value-box input{
        width:100% !important;
        height:38px !important;
        border:1px solid #d1d1d1 !important;
        border-radius:12px !important;
        padding:0 12px !important;
        font-size:14px !important;
      }

      .ais-page .ais-RefinementList-item--selected,
      .ais-page .ais-facet--item:not(.ais-HierarchicalMenu-item--parent):hover{
        border-left:none !important;
      }
      `;

      const style = document.createElement("style");
      style.setAttribute("data-ais-force-open", "1");
      style.appendChild(document.createTextNode(css));
      document.head.appendChild(style);
    })();

    /* ──────────────────────────────────────────────────────────────
      RE-APPLY
    ────────────────────────────────────────────────────────────── */

    function bindGlobalDirectFacetHandler() {
      if (window.__AIS_DIRECT_FACET_GLOBAL_BOUND__) return;
      window.__AIS_DIRECT_FACET_GLOBAL_BOUND__ = true;

      document.addEventListener(
        "click",
        function (e) {
          const input = e.target.closest(".ais-direct-facet-checkbox");
          if (!input) return;

          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          const helper = algoliaShopify?.instantsearch?.search?.helper;
          if (!helper) return;

          const facet = input.dataset.facet;
          const value = input.dataset.value;
          if (!facet || !value) return;

          const currentDisj =
            helper.state?.disjunctiveFacetsRefinements?.[facet] || [];

          const currentConj =
            helper.state?.facetsRefinements?.[facet] || [];

          const isCurrentlySelected =
            currentDisj.includes(value) || currentConj.includes(value);

          const willCheck = !isCurrentlySelected;
          input.checked = willCheck;

          try {
            if (facet === "tags") {
              if (willCheck) {
                window.__AIS_EXTRA_TAG_FILTERS__.add(value);
              } else {
                window.__AIS_EXTRA_TAG_FILTERS__.delete(value);
              }

              helper.setPage(0);
              helper.search();

              setTimeout(() => {
                renderCustomSelectedTagRefinements();
                window.dispatchEvent(new CustomEvent("ais-filter-applied"));
                window.dispatchEvent(new CustomEvent("ais-rendered"));
              }, 120);

              return;
            }

            const selectedDisj =
              helper.state?.disjunctiveFacetsRefinements?.[facet] || [];

            const selectedConj =
              helper.state?.facetsRefinements?.[facet] || [];

            const isSelected =
              selectedDisj.includes(value) || selectedConj.includes(value);

            const isDisjunctive =
              Array.isArray(helper.state?.disjunctiveFacets) &&
              helper.state.disjunctiveFacets.includes(facet);

            if (isDisjunctive) {
              if (willCheck && !isSelected) {
                helper.addDisjunctiveFacetRefinement(facet, value);
              } else if (!willCheck && isSelected) {
                helper.removeDisjunctiveFacetRefinement(facet, value);
              }
            } else {
              if (willCheck && !isSelected) {
                helper.addFacetRefinement(facet, value);
              } else if (!willCheck && isSelected) {
                helper.removeFacetRefinement(facet, value);
              }
            }

            helper.setPage(0);
            helper.search();

            setTimeout(() => {
              window.dispatchEvent(new CustomEvent("ais-filter-applied"));
              window.dispatchEvent(new CustomEvent("ais-rendered"));
            }, 120);
          } catch (err) {
            console.warn("[AIS] global direct facet failed", {
              facet,
              value,
              checked: willCheck,
              err,
            });
          }
        },
        true
      );
    }

    async function applyAllAISFixes() {
      try {
        await waitForAIS();
        bindGlobalDirectFacetHandler();
        applyFacetUIFixes();
        ensurePriceSortOptions();
      } catch {}
    }

    window.__AIS_APPLY_TEMPLATES__ = applyAllAISFixes;

    window.addEventListener("improved-search:opened", () => setTimeout(applyAllAISFixes, 0));
    window.addEventListener("improved-search:ready", () => setTimeout(applyAllAISFixes, 0));
    window.addEventListener("ais-filter-applied", () => setTimeout(applyAllAISFixes, 0));
    window.addEventListener("ais-rendered", () => {
      setTimeout(applyFacetUIFixes, 0);
      setTimeout(ensurePriceSortOptions, 0);
    });

    window.addEventListener("load", () => setTimeout(ensurePriceSortOptions, 0));

    /* ────────────────────────── PRODUCT TEMPLATE ────────────────────────── */
    algoliaShopify.hooks.registerHook(
      "beforeInstantSearchProductTemplate",
      function (_defaultTemplate, hit, html, components) {
        const vendorHandle =
          "vendor-" +
          (hit.vendor || "")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "") +
          ".png";

        return html`
          <div
            class="ais-hit ais-product relative"
            data-algolia-index="${hit.index}"
            data-algolia-position="${hit.productPosition}"
            data-algolia-queryid="${hit.queryID}"
            data-algolia-objectid="${hit.objectID}"
            data-variant-id="${hit.objectID}"
            data-distinct="${hit._distinct}"
            data-product-id="${hit.id}"
            data-handle="${hit.handle}"
            onClick="${() => algoliaShopify.helpers.handleItemClick(hit)}"
          >
            ${(() => {
              const hitTags = Array.isArray(hit.tags) ? hit.tags.map(t => t.toLowerCase()) : [];
              const isEcono  = hitTags.includes("econo-20");
              const isPreOrder = hitTags.includes("pre-order");
              const topLabel = isEcono ? "20% off installation"
                             : isPreOrder ? "Special Discount"
                             : "Best price in the area";
              return html`
                <div class="pdp-best-price-announcement absolute w-auto !py-[3px] !px-[10px] top-2 right-1 z-10 !text-[12px]">
                  ${topLabel}
                </div>
              `;
            })()}

            <a
              class="relative picture-container !rounded-[20px]"
              data-algolia-index="${hit.index}"
              data-algolia-position="${hit.productPosition}"
              data-algolia-queryid="${hit.queryID}"
              data-algolia-objectid="${hit.objectID}"
              href="/products/${hit.handle}"
              title="${algoliaShopify.helpers.fullTitle(hit.title, hit._distinct, hit.variant_title)}"
            >
              <img
                class="ais-hit--picture"
                src="${algoliaShopify.helpers.mediumImage(hit)}"
                alt="${hit.title} - ${hit.variant_title}"
              />
            </a>

            <div class="flex flex-col gap-1 hit-info-wrapper">
              ${hit.vendor &&
              (() => {
                const hitTags = Array.isArray(hit.tags) ? hit.tags.map(t => t.toLowerCase()) : [];
                const isEcono    = hitTags.includes("econo-20");
                const isPreOrder = hitTags.includes("pre-order");
                const isZeta     = hitTags.includes("zeta");
                const bottomLabel = isEcono    ? "Pick-Up at Econo-Tires"
                                  : isPreOrder ? "Pick-Up at Venue"
                                  : isZeta     ? "Fast delivery"
                                  : "Free delivery";

                return html`
                  <div class="flex w-full justify-between !mt-[-50px] z-10 !px-1">
                    <div class="vendor bg-white rounded-[20px] !px-2 !py-1 flex items-center">
                      <img
                        src="https://dantetires.com/cdn/shop/files/${vendorHandle}"
                        alt="${hit.vendor &&
                        components.Highlight({ attribute: "vendor", hit })}"
                        class="!h-[20px] w-auto !m-auto mb-[-20px] z-10"
                      />
                    </div>

                    <div class="pdp-shipping-promise !text-black !text-[12px] bg-white rounded-[20px] !px-2 !py-1 flex items-center gap-1">
                      ${(isEcono || isPreOrder) ? html`
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-[20px] h-[20px] shrink-0" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                        </svg>
                      ` : html`
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-[20px] h-[20px] shrink-0" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                        </svg>
                      `}
                      ${bottomLabel}
                    </div>
                  </div>
                `;
              })()}

              <h3 class="!text-black">
                <a
                  class="!text-black"
                  data-algolia-index="${hit.index}"
                  data-algolia-position="${hit.productPosition}"
                  data-algolia-queryid="${hit.queryID}"
                  data-algolia-objectid="${hit.objectID}"
                  href="/products/${hit.handle}"
                  title="${algoliaShopify.helpers.fullTitle(hit.title, hit._distinct, hit.variant_title)}"
                >
                  ${components.Highlight({ attribute: "title", hit })}
                  ${algoliaShopify.helpers.variantTitleAddition(hit, hit._distinct)}
                </a>
              </h3>

              <div class="price !my-0">
                <div class="price-main font-medium text-4xl leading-8" prev-class="ais-hit--price">
                  <b>
                    ${algoliaShopify.helpers.displayPrice(hit, hit._distinct)}
                    <small class="pr-2">/tire</small>
                  </b>

                  ${!hit._distinct &&
                  html`
                    <span class="text-base text-[#545454] text-xl line-through pl-3" prev-class="ais-hit--price-striked">
                      <span>${algoliaShopify.helpers.displayStrikedPrice(
                        hit.price,
                        hit.compare_at_price
                      )}</span>
                    </span>
                  `}
                </div>
              </div>

              ${!hit._distinct &&
              html`
                <form
                  id="algolia-add-to-cart-${hit.objectID}"
                  style="display:none;"
                  action="/cart/add"
                  method="post"
                  enctype="multipart/form-data"
                >
                  <input type="hidden" name="id" value="${hit.objectID}" />
                  <input type="hidden" name="quantity" value="4" />
                </form>

                <p class="ais-hit--cart">
                  ${hit.can_order
                    ? html`
                        <button class="ais-hit--cart-button !bg-red-600" data-form-id="algolia-add-to-cart-${hit.objectID}">
                          ${algoliaShopify.translations.addToCart}
                        </button>
                      `
                    : html`
                        <button class="ais-hit--cart-button ais-hit--cart-button__disabled !bg-neutral-400">
                          ${algoliaShopify.translations.outOfStock}
                        </button>
                      `}
                </p>
              `}
            </div>
          </div>
        `;
      }
    );

    /* ────────────────────────── STATS TEMPLATE ────────────────────────── */
    algoliaShopify.hooks.registerHook(
      "beforeInstantSearchStatsTemplate",
      function (_defaultTemplate, data, html) {
        window.dispatchEvent(new CustomEvent("ais-filter-applied"));
        setTimeout(applyFacetUIFixes, 0);
        setTimeout(ensurePriceSortOptions, 0);

        return html`
          ${data.hasOneResult &&
          html`<span class="ais-stats--nb-results !text-black">1 result found</span>`}
          ${data.hasManyResults &&
          html`
            ${data.page * data.hitsPerPage + 1} - ${" "}
            ${Math.min((data.page + 1) * data.hitsPerPage, data.nbHits)} ${" "}
            out of
            <span class="ais-stats--nb-results__ias font-medium !text-black">
              ${" "} ${algoliaShopify.helpers.formatNumber(data.nbHits)} ${" "}
              results found
            </span>
          `}
        `;
      }
    );

    /* ────────────────────────── MAIN TEMPLATE ────────────────────────── */
    algoliaShopify.hooks.registerHook(
      "beforeInstantSearchMainTemplate",
      function (_defaultTemplate, data, html) {
        algoliaShopify.translations.searchForProduct =
          "Search by vehicles, tire size or brand";

        const allFacets = [
          ...(data.facets || []),
          ...(data.disjunctiveFacets || []),
        ];

        const vendorFacet = getFacetByName(allFacets, "vendor");
        const productTypeFacet = getFacetByName(allFacets, "product_type");
        const tagsFacet = getFacetByName(allFacets, "tags");
        const priceFacet = getFacetByName(allFacets, "price");
        const priceRangeFacet = getFacetByName(allFacets, "price_range");

        setTimeout(() => {
          applyFacetUIFixes();
          ensurePriceSortOptions();
          window.dispatchEvent(new CustomEvent("ais-rendered"));
        }, 0);

        return html`
          <div class="ais-page !text-black">
            <div class="ais-input fitment-anchor">
              <div class="ais-search-box-container">
                <label class="floating">
                  ${algoliaShopify.translations.searchForProduct}
                </label>
              </div>
              <div class="ais-input-button">
                <div class="ais-clear-input-icon !text-black"></div>
              </div>

              <div class="fitment-suggestions hidden" data-fitment-suggestions>
                <div class="fitment-title">Vehicle / Fitment</div>
                <div class="fitment-items" data-fitment-items></div>
              </div>
            </div>

            <div class="ais-facets-button !text-black">
              Show filters
            </div>

            <div class="ais-facets !text-black">
              <div class="relative title-wrapper">
                <h2>Filter <span class="md:hidden"></span></h2>
                <p class="results-count md:hidden"></p>
                <div class="ais-clear-refinements-container"></div>
                <div class="ais-facets-button-modal hidden"></div>
              </div>

              <div class="ais-current-refined-values-container !text-black"></div>

              <div class="ais-facet-dropdown-wrapper !text-black ais-facet-price-custom">
                <label
                  for="custom-price-range"
                  class="ais-range-slider--header ais-facets--header ais-header my-1 font-bold !text-black"
                >
                  Price Range
                </label>
                <input
                  class="ais-dropdown-checkbox !text-black"
                  type="checkbox"
                  id="custom-price-range"
                  name="dropdown"
                />
                <div class="ais-facet-dropdown-container !text-black" data-price-range-host></div>
              </div>

              <div class="ais-facet-dropdown-wrapper !text-black ais-facet-refinementList ais-facet-vendor-custom">
                <label
                  for="direct-vendor"
                  class="ais-range-slider--header ais-facets--header ais-header my-1 font-bold !text-black"
                >
                  Brand
                </label>
                <input
                  class="ais-dropdown-checkbox !text-black"
                  type="checkbox"
                  id="direct-vendor"
                  name="dropdown"
                />
                <div class="ais-facet-dropdown-container !text-black ais-custom-facet-container" data-direct-facet-host="vendor"></div>
              </div>

              <div class="ais-facet-dropdown-wrapper !text-black ais-facet-refinementList ais-facet-product-type-custom">
                <label
                  for="direct-product-type"
                  class="ais-range-slider--header ais-facets--header ais-header my-1 font-bold !text-black"
                >
                  Category
                </label>
                <input
                  class="ais-dropdown-checkbox !text-black"
                  type="checkbox"
                  id="direct-product-type"
                  name="dropdown"
                />
                <div class="ais-facet-dropdown-container !text-black ais-custom-facet-container" data-direct-facet-host="product_type"></div>
              </div>

              ${[
                ["type-tires", "Type Tires"],
                ["rim-size", "Rim Size"],
                ["speed", "Speed"],
                ["tread", "Tread"],
                ["aspect", "Aspect"],
                ["section", "Section"],
                ["load", "Load"],
              ].map(
                ([key, title]) => html`
                  <div class="ais-facet-dropdown-wrapper !text-black ais-facet-custom-${key}" data-custom-facet="${key}">
                    <label
                      for="custom-${key}"
                      class="ais-range-slider--header ais-facets--header ais-header my-1 font-bold !text-black"
                    >
                      ${title}
                    </label>
                    <input
                      class="ais-dropdown-checkbox !text-black"
                      type="checkbox"
                      id="custom-${key}"
                      name="dropdown"
                    />
                    <div class="ais-facet-dropdown-container !text-black ais-custom-facet-container"></div>
                  </div>
                `
              )}

              ${vendorFacet
                ? html`
                    <div class="ais-facet-source-hidden">
                      <div class="ais-facet-${vendorFacet.escapedName}-container"></div>
                    </div>
                  `
                : ""}

              ${productTypeFacet
                ? html`
                    <div class="ais-facet-source-hidden">
                      <div class="ais-facet-${productTypeFacet.escapedName}-container"></div>
                    </div>
                  `
                : ""}

              ${tagsFacet
                ? html`
                    <div class="ais-facet-source-hidden">
                      <div class="ais-facet-${tagsFacet.escapedName}-container"></div>
                    </div>
                  `
                : ""}

              ${priceFacet
                ? html`
                    <div class="ais-facet-source-hidden">
                      <div class="ais-facet-${priceFacet.escapedName}-container"></div>
                    </div>
                  `
                : ""}

              ${priceRangeFacet
                ? html`
                    <div class="ais-facet-source-hidden">
                      <div class="ais-facet-${priceRangeFacet.escapedName}-container"></div>
                    </div>
                  `
                : ""}
            </div>

            <div class="ais-block">
              <div class="ais-search-header">
                <div class="ais-stats-container"></div>

                <div class="ais-change-display">
                  <span class="ais-change-display-block ais-change-display-selected !text-black"></span>
                  <span class="ais-change-display-list !text-black"></span>
                </div>

                <div class="ais-sort !text-black">
                  Sort By
                  <span class="ais-sort-orders-container"></span>
                </div>
              </div>

              <div class="ais-hits-container ais-results-as-block"></div>
              <div class="ais-pagination-container"></div>
            </div>
          </div>
        `;
      }
    );

    try {
      const search = algoliaShopify?.instantsearch?.search;
      if (search && typeof search.refresh === "function") search.refresh();
      else if (search && search.helper && typeof search.helper.search === "function") {
        search.helper.search();
      }
    } catch (e) {}

    setTimeout(ensurePriceSortOptions, 0);
    setTimeout(ensurePriceSortOptions, 250);
    setTimeout(ensurePriceSortOptions, 900);
  }

  document.addEventListener("algolia.hooks.initialize", initAISHooks);

  let tries = 0;
  (function boot() {
    tries++;
    if (window.__AIS_HOOKS_READY__) return;
    initAISHooks();
    if (!window.__AIS_HOOKS_READY__ && tries < 80) {
      requestAnimationFrame(boot);
    }
  })();

  window.addEventListener("improved-search:opened", () => setTimeout(initAISHooks, 0));
  window.addEventListener("improved-search:ready", () => setTimeout(initAISHooks, 0));
})();